<script setup lang="ts">
/**
 * Рантайм-флаги парой тумблеров на среду — как в макете «Флаги фич».
 *
 * Источник правды — git: флаг переключается коммитом в features/dev.json или
 * features/prod.json репозитория инфраструктуры. Трекер показывает состояние и
 * дёргает ту же ручку, поэтому тумблер ждёт ответа сервиса, а не рисует
 * желаемое сразу.
 */
import { AppModal, AppSwitch } from '@vinprop/design-system'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { FLAG_ENVIRONMENTS, listFlags, setFlag, toFlagRows, type FlagEnvironment, type FlagRow } from '../api'

const emit = defineEmits<{ failed: [error: unknown]; close: [] }>()

const { t } = useI18n()

const rows = ref<FlagRow[]>([])
const loading = ref(true)
/** Ключи, по которым сейчас ждём ответа: тумблер на это время заперт */
const pending = ref(new Set<string>())

const load = async () => {
  loading.value = true

  try {
    rows.value = toFlagRows(await listFlags())
  } catch (failure) {
    emit('failed', failure)
  } finally {
    loading.value = false
  }
}

const toggle = async (row: FlagRow, environment: FlagEnvironment, enabled: boolean) => {
  const lock = `${row.key}:${environment}`

  pending.value = new Set([...pending.value, lock])

  try {
    await setFlag(row.key, environment, enabled)
    row.enabled[environment] = enabled
  } catch (failure) {
    emit('failed', failure)
  } finally {
    const next = new Set(pending.value)

    next.delete(lock)
    pending.value = next
  }
}

const isPending = (row: FlagRow, environment: FlagEnvironment) =>
  pending.value.has(`${row.key}:${environment}`)

onMounted(load)
</script>

<template>
  <AppModal model-value :title="t('devops.flags.title')" size="medium" @update:model-value="emit('close')">
    <div class="flags">
    <p class="flags__note">{{ t('devops.flags.note') }}</p>

    <p v-if="loading" class="flags__empty">{{ t('devops.flags.loading') }}</p>
    <p v-else-if="!rows.length" class="flags__empty">{{ t('devops.flags.empty.title') }}</p>

    <table v-else class="flags__table">
      <thead>
        <tr>
          <th>{{ t('devops.flags.columns.flag') }}</th>
          <th>{{ t('devops.flags.columns.what') }}</th>
          <th v-for="environment in FLAG_ENVIRONMENTS" :key="environment" class="flags__env">
            {{ environment.toUpperCase() }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.key">
          <td class="flags__key">{{ row.key }}</td>
          <td class="flags__what">{{ row.description }}</td>
          <td v-for="environment in FLAG_ENVIRONMENTS" :key="environment" class="flags__env">
            <AppSwitch
              :model-value="row.enabled[environment]"
              :disabled="isPending(row, environment)"
              size="small"
              @update:model-value="toggle(row, environment, $event)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    </div>
  </AppModal>
</template>

<style scoped>
.flags {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.flags__note,
.flags__empty {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.flags__table {
  width: 100%;
  border-collapse: collapse;
}

.flags__table th {
  padding: 0 12px 8px;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  text-align: left;
  text-transform: uppercase;
}

.flags__table td {
  padding: 10px 12px;
  border-top: 1px solid var(--color-border);
  font-size: var(--font-size-sm);
  vertical-align: middle;
}

.flags__key {
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
}

.flags__what {
  color: var(--color-text-secondary);
}

.flags__env {
  width: 64px;
  text-align: center;
}
</style>
